import { hasNotificationApi } from '@/lib/platform/capabilities'
import { setBrowserEnabled, setSoundEnabled } from './prefs'

const SOUND_PATH = '/sounds/notification.mp3'
/** Audível o bastante pro browser contar como reprodução, inaudível pro humano. */
const WARM_UP_VOLUME = 0.0001

export interface ActivationInput {
  wantSound: boolean
  wantBrowser: boolean
}

export interface ActivationResult {
  soundEnabled: boolean
  browserEnabled: boolean
  /** `'unsupported'` = a interface `Notification` não existe (aba do Safari iOS). */
  permission: NotificationPermission | 'unsupported'
}

/**
 * Toca o som inaudível só pra destravar o contexto de áudio da aba.
 *
 * **Tem que ser chamado na pilha do handler de clique/touch** (P6: o WebKit exige
 * `play()` dentro do handler; o Chrome trata a ativação como sticky). Por isso
 * `new Audio()` e `.play()` acontecem na parte SÍNCRONA desta função, antes de
 * qualquer `await`.
 *
 * Não escreve preferência nenhuma — quem decide gravar é o call site.
 */
export async function warmUpNotificationSound(): Promise<void> {
  if (typeof Audio === 'undefined') return
  try {
    const audio = new Audio(SOUND_PATH)
    audio.volume = WARM_UP_VOLUME
    await audio.play().catch(() => {})
  } catch {
    /* sem áudio disponível — não é falha de ativação */
  }
}

/**
 * O gesto único de "como quer ser avisado": pede permissão e destrava o áudio na
 * MESMA ativação do usuário, e grava as prefs conforme o veredito.
 *
 * **Ordem é contrato, não estilo.** `Notification.requestPermission()` precisa
 * ser chamado ainda dentro da user-activation do clique; qualquer `await` antes
 * (o warm-up de áudio é o suspeito de sempre) invalida o gesto em alguns
 * browsers e o prompt nunca abre. Por isso a promise nasce primeiro e é
 * resolvida depois.
 *
 * `browserEnabled` só liga quando o browser **concedeu** — marcar o checkbox não
 * basta, senão a pref mente e a tela mostra "ligado" com o SO calado.
 */
export async function activateNotifications(
  input: ActivationInput,
): Promise<ActivationResult> {
  // Detecção vem de `lib/platform/capabilities` — mesma pergunta, uma resposta só.
  // `typeof` e não `in`: `vi.stubGlobal('Notification', undefined)` deixa a chave
  // existindo com valor `undefined`, e `in` devolveria `true`.
  const suportaNotificacao = hasNotificationApi()

  let pedido: Promise<NotificationPermission> | null = null
  if (input.wantBrowser && suportaNotificacao && Notification.permission === 'default') {
    pedido = Notification.requestPermission()
  }

  let soundEnabled = false
  if (input.wantSound) {
    setSoundEnabled(true)
    soundEnabled = true
    await warmUpNotificationSound()
  }

  let permission: NotificationPermission | 'unsupported' = 'unsupported'
  let browserEnabled = false
  if (suportaNotificacao) {
    permission = pedido ? await pedido : Notification.permission
    if (input.wantBrowser) {
      browserEnabled = permission === 'granted'
      setBrowserEnabled(browserEnabled)
    }
  }

  return { soundEnabled, browserEnabled, permission }
}
